import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, AlertTriangle, ShieldAlert, MessageSquareQuote,
  CheckCircle2, XCircle, Target, HelpCircle, Sparkles, Save, BadgeCheck, Download,
  Upload, Trash2, FileText, Image as ImageIcon, ShieldCheck, FlaskConical, GitCompare,
  TrendingUp, TrendingDown, Loader2, Send, Network, Cpu, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { IndexRing, MaturityBadge, ScoreRadar, AgenticMap } from "@/components/AgenticVisuals";
import { EvidenceImage } from "@/components/EvidenceImage";

const AVATARS = [
  "https://images.unsplash.com/photo-1584940120505-117038d90b05?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
  "https://images.unsplash.com/photo-1614786269829-d24616faf56d?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
];

function Section({ title, children, icon: Icon }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-6">
      <div className="flex items-center gap-2 mb-4 text-slate-200">
        {Icon && <Icon size={18} strokeWidth={1.5} className="text-primary" />}
        <h3 className="font-heading text-lg font-medium tracking-tight">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function List({ items, iconColor = "text-primary", icon: Icon = Sparkles }) {
  if (!items || items.length === 0) return <p className="text-sm text-slate-500 italic">Sin elementos.</p>;
  return (
    <ul className="space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3 text-sm text-slate-300">
          <Icon size={16} className={`${iconColor} mt-0.5 shrink-0`} strokeWidth={1.5} />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

export default function AssessmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const isConsultant = user?.role === "consultant" || user?.role === "admin";

  const { data: a, isLoading, isError } = useQuery({
    queryKey: ["assessment", id],
    queryFn: async () => (await api.get(`/assessments/${id}`)).data,
    refetchInterval: (query) => (query.state.data?.status === "processing" ? 4000 : false),
  });

  const [notes, setNotes] = useState("");
  const [recommendation, setRecommendation] = useState("");
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/assessments/${id}/report.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `TalentHackers_Informe_${(a?.candidate_name || "candidato").replace(/\s/g, "_")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Informe PDF descargado");
    } catch (e) {
      toast.error("No se pudo generar el PDF");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (a?.consultant_notes) {
      setNotes(a.consultant_notes.notes || "");
      setRecommendation(a.consultant_notes.final_recommendation || "");
    }
  }, [a]);

  const saveMut = useMutation({
    mutationFn: (payload) => api.patch(`/assessments/${id}/consultant-notes`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assessment", id] });
      qc.invalidateQueries({ queryKey: ["assessments"] });
      toast.success("Notas guardadas");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["assessment", id] });

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/assessments/${id}/evidence`, fd);
      toast.success("Evidencia analizada y añadida");
      invalidate();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteEvidenceMut = useMutation({
    mutationFn: (eid) => api.delete(`/assessments/${id}/evidence/${eid}`),
    onSuccess: () => { invalidate(); toast.success("Evidencia eliminada"); },
  });

  const verifyMut = useMutation({
    mutationFn: () => api.post(`/assessments/${id}/verify`),
    onSuccess: () => { invalidate(); toast.success("Verificación completada"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error en la verificación"),
  });

  const genCaseMut = useMutation({
    mutationFn: () => api.post(`/assessments/${id}/practical-case`),
    onSuccess: () => { invalidate(); toast.success("Prueba práctica generada"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const [practicalAnswer, setPracticalAnswer] = useState("");
  const submitPracticalMut = useMutation({
    mutationFn: () => api.post(`/assessments/${id}/practical-submission`, { response_text: practicalAnswer }),
    onSuccess: () => { invalidate(); toast.success("Respuesta evaluada"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  if (isLoading) return <div className="text-slate-500">Cargando evaluación…</div>;
  if (isError || !a) return <div className="text-red-400">No se pudo cargar la evaluación.</div>;

  const ev = a.evaluation || {};
  const cr = ev.consultant_report || {};
  const clr = ev.client_report || {};
  const evidences = a.evidences || [];
  const ver = a.verification;
  const pc = a.practical_case;
  const fileUrl = (p) => `${API}/files/${p}?auth=${encodeURIComponent(localStorage.getItem("token") || "")}`;
  const avatar = AVATARS[(a.candidate_name?.length || 0) % AVATARS.length];

  // Candidate sees only their journey/status — NO scoring, NO reports.
  if (!isConsultant) {
    const st = a.status;
    const isDraft = st === "draft";
    const isProcessing = st === "processing";
    const isErr = st === "error";
    const isSubmitted = !isDraft && !isProcessing && !isErr;
    const q = a.questionnaire || {};
    const badgeLabel = isDraft ? "Borrador" : isProcessing ? "Procesando…" : isErr ? "Error al procesar" : "Enviado · En revisión";
    const badgeClass = isSubmitted ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10"
      : isProcessing ? "border-cyan-500/40 text-cyan-400 bg-cyan-500/10"
      : isErr ? "border-red-500/40 text-red-400 bg-red-500/10"
      : "border-amber-500/40 text-amber-400 bg-amber-500/10";
    const statusText = isSubmitted
      ? "Tu evaluación se ha enviado correctamente. El consultor la está revisando; los resultados detallados son confidenciales y los gestiona el equipo de Talent Hackers."
      : isProcessing
        ? "Tu evaluación se ha enviado y el Deputy la está procesando. Esto puede tardar un par de minutos; esta página se actualizará automáticamente."
        : isErr
          ? "Hubo un problema al procesar tu evaluación. Vuelve a enviarla y, si el problema persiste, contacta con el equipo de Talent Hackers."
          : "Esta candidatura está en borrador. Completa y envía tu evaluación.";
    return (
      <div className="max-w-2xl">
        <button onClick={() => navigate(-1)} data-testid="back-btn" className="flex items-center gap-2 text-sm text-slate-400 hover:text-primary mb-6 transition-colors">
          <ArrowLeft size={16} /> Volver
        </button>
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 mb-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight">{a.target_role}</h1>
              <p className="text-sm text-slate-500 font-mono mt-1">{new Date(a.created_at).toLocaleDateString("es-ES")}</p>
            </div>
            <span data-testid="candidate-status" className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${badgeClass}`}>
              {isProcessing ? <Loader2 size={15} className="animate-spin" /> : <BadgeCheck size={15} />} {badgeLabel}
            </span>
          </div>
          <p className="text-sm text-slate-400 mt-4">{statusText}</p>
          {isErr && (
            <Button data-testid="resubmit-btn" onClick={async () => {
              try { await api.post(`/assessments/${id}/submit`); toast.success("Reenviado. Procesando…"); qc.invalidateQueries({ queryKey: ["assessment", id] }); }
              catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
            }} className="mt-4 gap-2 bg-primary text-slate-950 hover:bg-cyan-400"><Send size={16} /> Reenviar evaluación</Button>
          )}
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 space-y-4">
          <h3 className="font-heading text-lg font-medium tracking-tight flex items-center gap-2"><FileText size={16} className="text-primary" /> Tu candidatura</h3>
          <div className="text-sm">
            <div className="text-slate-500 mb-1">Herramientas</div>
            <p className="text-slate-300">{q.tools_used}</p>
          </div>
          <div className="text-sm">
            <div className="text-slate-500 mb-1">Agentes configurados</div>
            <p className="text-slate-300">{q.agents_configured}</p>
          </div>
          {(a.adaptive_questions || []).filter((x) => x.answer).length > 0 && (
            <div className="text-sm">
              <div className="text-slate-500 mb-1">Preguntas de seguimiento</div>
              <ul className="space-y-2">
                {(a.adaptive_questions || []).filter((x) => x.answer).map((x, i) => (
                  <li key={i} className="text-slate-300"><span className="text-slate-400">{x.question}</span><br />{x.answer}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="text-sm">
            <div className="text-slate-500 mb-2">Evidencias aportadas ({evidences.length})</div>
            <div className="space-y-2">
              {evidences.length === 0 && <p className="text-slate-600 italic">Ninguna</p>}
              {evidences.map((e) => (
                <a key={e.id} href={fileUrl(e.storage_path)} target="_blank" rel="noreferrer" data-testid={`my-evidence-${e.id}`} className="flex items-center gap-2 text-sm text-primary hover:underline">
                  <ExternalLink size={14} /> {e.filename}
                </a>
              ))}
            </div>
          </div>
          {pc?.submission?.response_text && (
            <div className="text-sm">
              <div className="text-slate-500 mb-1">Prueba práctica</div>
              <p className="text-slate-300 whitespace-pre-wrap">{pc.submission.response_text}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => navigate(-1)} data-testid="back-btn" className="flex items-center gap-2 text-sm text-slate-400 hover:text-primary mb-6 transition-colors">
        <ArrowLeft size={16} /> Volver
      </button>

      {/* Header */}
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-6 justify-between">
          <div className="flex items-center gap-5">
            <img src={avatar} alt="" className="h-16 w-16 rounded-full object-cover border border-slate-700 grayscale" />
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">{a.candidate_name}</h1>
              <p className="text-slate-400">{a.target_role}</p>
              <div className="mt-2 flex items-center gap-3">
                <MaturityBadge maturity={ev.maturity} />
                {a.consultant_notes?.validated && (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-400 border border-emerald-500/30 rounded-full px-2.5 py-1">
                    <BadgeCheck size={13} /> Validado
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <IndexRing value={ev.agentic_index || 0} />
            <div className="hidden sm:block max-w-[150px]">
              <div className="text-xs uppercase tracking-wider text-primary">Agentic Productivity Index</div>
              <div className="text-xs text-slate-500 mt-1">{ev.maturity?.tagline}</div>
              {ver && (
                <div className="mt-2 text-xs text-slate-400">
                  Verificado: <span className="font-mono font-bold text-primary">{ver.verified_index?.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="summary">
        <TabsList className="bg-slate-900 border border-slate-800 flex-wrap h-auto">
          <TabsTrigger value="summary" data-testid="tab-summary">Resumen</TabsTrigger>
          <TabsTrigger value="map" data-testid="tab-map">Mapa agentic</TabsTrigger>
          <TabsTrigger value="matrix" data-testid="tab-matrix">Matriz</TabsTrigger>
          <TabsTrigger value="evidence" data-testid="tab-evidence">
            Evidencias{evidences.length > 0 && <span className="ml-1.5 rounded-full bg-primary/20 text-primary px-1.5 text-[10px] font-mono">{evidences.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="verification" data-testid="tab-verification">Verificación</TabsTrigger>
          <TabsTrigger value="practical" data-testid="tab-practical">Prueba práctica</TabsTrigger>
          <TabsTrigger value="interview" data-testid="tab-interview">Entrevista</TabsTrigger>
          <TabsTrigger value="consultant" data-testid="tab-consultant">Informe consultor</TabsTrigger>
          <TabsTrigger value="client" data-testid="tab-client">Informe cliente</TabsTrigger>
        </TabsList>

        {/* Summary */}
        <TabsContent value="summary" className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Section title="Puntuación por dimensión" icon={Target}>
            <ScoreRadar dimensions={ev.dimensions} />
          </Section>
          <div className="space-y-5">
            <Section title="Fortalezas" icon={CheckCircle2}>
              <List items={cr.strengths} icon={CheckCircle2} iconColor="text-emerald-400" />
            </Section>
            <Section title="Debilidades y dudas" icon={XCircle}>
              <List items={cr.weaknesses} icon={XCircle} iconColor="text-amber-400" />
            </Section>
          </div>
          {ev.inconsistencies?.length > 0 && (
            <div className="lg:col-span-2">
              <Section title="Inconsistencias detectadas" icon={AlertTriangle}>
                <List items={ev.inconsistencies} icon={AlertTriangle} iconColor="text-amber-400" />
              </Section>
            </div>
          )}
        </TabsContent>

        {/* Map */}
        <TabsContent value="map" className="mt-6">
          <Section title="Mapa agentic del candidato" icon={Target}>
            <p className="text-sm text-slate-400 mb-5 -mt-2">Flujo del stack: entrada → procesamiento → producción → ejecución → control.</p>
            <AgenticMap map={ev.agentic_map} />
          </Section>
        </TabsContent>

        {/* Matrix */}
        <TabsContent value="matrix" className="mt-6">
          <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
            <table className="w-full text-sm" data-testid="matrix-table">
              <thead>
                <tr className="border-b border-slate-800 text-left text-slate-400">
                  <th className="px-5 py-3 font-medium">Dimensión</th>
                  <th className="px-5 py-3 font-medium text-center">Peso</th>
                  <th className="px-5 py-3 font-medium text-center">Score</th>
                  <th className="px-5 py-3 font-medium hidden md:table-cell">Justificación</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {ev.dimensions?.map((d) => (
                  <tr key={d.key} className="hover:bg-slate-800/50 transition-colors duration-200">
                    <td className="px-5 py-3.5 font-medium text-slate-100">{d.name}</td>
                    <td className="px-5 py-3.5 text-center font-mono text-slate-500">{d.weight}%</td>
                    <td className="px-5 py-3.5 text-center">
                      <span className={`font-mono font-bold ${d.score >= 4 ? "text-emerald-400" : d.score >= 2.5 ? "text-primary" : "text-slate-400"}`}>
                        {d.score.toFixed(1)}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-slate-400 hidden md:table-cell">{d.justification}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Evidence */}
        <TabsContent value="evidence" className="mt-6 space-y-5">
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="font-heading text-lg font-medium tracking-tight text-slate-100 flex items-center gap-2">
                  <Upload size={18} className="text-primary" strokeWidth={1.5} /> Pruebas verificables
                </h3>
                <p className="text-sm text-slate-400 mt-1 max-w-xl">
                  Sube capturas de tus agentes/workflows, exportaciones (n8n/Make .json/.yaml), PDFs o documentos. El Deputy los analiza técnicamente para verificar la orquestación real.
                </p>
                <p className="text-xs text-amber-400/80 mt-2">No subas nunca contraseñas, API keys ni datos confidenciales de terceros.</p>
              </div>
              <div>
                <input ref={fileRef} type="file" onChange={handleUpload} className="hidden"
                  accept=".png,.jpg,.jpeg,.webp,.gif,.pdf,.md,.txt,.json,.yaml,.yml,.csv" data-testid="evidence-file-input" />
                <Button data-testid="evidence-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()}
                  className="gap-2 bg-primary text-slate-950 hover:bg-cyan-400">
                  {uploading ? <><Loader2 size={16} className="animate-spin" /> Analizando…</> : <><Upload size={16} /> Subir evidencia</>}
                </Button>
              </div>
            </div>
          </div>

          {evidences.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-800 bg-slate-900/40 p-10 text-center text-slate-500">
              <FileText className="mx-auto mb-3 text-slate-600" size={36} strokeWidth={1.5} />
              Aún no hay evidencias. Sube pruebas para poder verificar el stack técnicamente.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {evidences.map((e) => {
                const an = e.analysis || {};
                const orch = an.orchestration || {};
                return (
                  <div key={e.id} className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid={`evidence-${e.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        {e.is_image ? <ImageIcon size={16} className="text-primary shrink-0" /> : <FileText size={16} className="text-primary shrink-0" />}
                        <span className="text-sm font-medium text-slate-100 truncate">{e.filename}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-mono text-sm font-bold text-primary">{(an.authenticity_score ?? 0).toFixed(1)}<span className="text-slate-600 text-xs">/5</span></span>
                        <button data-testid={`delete-evidence-${e.id}`} onClick={() => deleteEvidenceMut.mutate(e.id)} className="text-slate-500 hover:text-red-400 transition-colors">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 border border-slate-700 rounded px-1.5 py-0.5">{an.category || e.category}</span>
                      <span className="text-[10px] text-slate-600">por {e.uploaded_by}</span>
                    </div>

                    <a
                      href={fileUrl(e.storage_path)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`open-evidence-${e.id}`}
                      className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline mt-2"
                    >
                      <ExternalLink size={13} /> Abrir documento original
                    </a>

                    {e.is_image && (
                      <a href={fileUrl(e.storage_path)} target="_blank" rel="noreferrer" className="block mt-3" title="Abrir a tamaño completo">
                        <EvidenceImage path={e.storage_path} alt={e.filename} />
                      </a>
                    )}

                    <p className="text-sm text-slate-300 mt-3">{an.summary}</p>

                    {an.technologies?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {an.technologies.map((t, i) => (
                          <span key={i} className="text-[11px] rounded bg-slate-800 text-slate-300 px-2 py-0.5">{t}</span>
                        ))}
                      </div>
                    )}

                    <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                      <div className="rounded bg-slate-950 border border-slate-800 p-2">
                        <div className="font-mono text-lg text-slate-100">{orch.nodes_detected ?? "—"}</div>
                        <div className="text-[10px] text-slate-500">Nodos</div>
                      </div>
                      <div className="rounded bg-slate-950 border border-slate-800 p-2">
                        <div className="font-mono text-lg text-slate-100">{orch.connections_detected ?? "—"}</div>
                        <div className="text-[10px] text-slate-500">Conexiones</div>
                      </div>
                      <div className="rounded bg-slate-950 border border-slate-800 p-2">
                        <div className="text-sm text-slate-100 capitalize mt-1">{orch.depth || "—"}</div>
                        <div className="text-[10px] text-slate-500">Profundidad</div>
                      </div>
                    </div>

                    {orch.patterns?.length > 0 && (
                      <div className="mt-3">
                        <div className="text-xs text-slate-500 mb-1">Patrones de orquestación</div>
                        <List items={orch.patterns} icon={Network} />
                      </div>
                    )}
                    {an.verified_capabilities?.length > 0 && (
                      <div className="mt-3">
                        <div className="text-xs text-slate-500 mb-1">Capacidades verificadas</div>
                        <List items={an.verified_capabilities} icon={CheckCircle2} iconColor="text-emerald-400" />
                      </div>
                    )}
                    {an.red_flags?.length > 0 && (
                      <div className="mt-3">
                        <div className="text-xs text-slate-500 mb-1">Señales de alarma</div>
                        <List items={an.red_flags} icon={AlertTriangle} iconColor="text-amber-400" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Verification */}
        <TabsContent value="verification" className="mt-6 space-y-5">
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-heading text-lg font-medium tracking-tight text-slate-100 flex items-center gap-2">
                <GitCompare size={18} className="text-primary" strokeWidth={1.5} /> Índice declarado vs. verificado
              </h3>
              <p className="text-sm text-slate-400 mt-1 max-w-xl">Contrasta lo declarado con las evidencias reales y recalcula el Agentic Productivity Index verificado.</p>
            </div>
            <Button data-testid="run-verify-btn" disabled={verifyMut.isPending} onClick={() => verifyMut.mutate()} className="gap-2 bg-primary text-slate-950 hover:bg-cyan-400">
              {verifyMut.isPending ? <><Loader2 size={16} className="animate-spin" /> Verificando…</> : <><ShieldCheck size={16} /> {ver ? "Re-verificar" : "Ejecutar verificación"}</>}
            </Button>
          </div>

          {!ver ? (
            <div className="rounded-xl border border-dashed border-slate-800 bg-slate-900/40 p-10 text-center text-slate-500">
              Ejecuta la verificación para comparar el índice declarado con el verificado por evidencias.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-xl border border-slate-800 bg-slate-900 p-6">
                  <div className="text-xs uppercase tracking-wider text-slate-400">Índice declarado</div>
                  <div className="font-mono text-4xl font-bold text-slate-300 mt-2">{ver.declared_index?.toFixed(2)}</div>
                </div>
                <div className="relative rounded-xl border border-slate-800 bg-slate-900 p-6 overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent" />
                  <div className="text-xs uppercase tracking-wider text-primary">Índice verificado</div>
                  <div className="font-mono text-4xl font-bold text-primary mt-2">{ver.verified_index?.toFixed(2)}</div>
                  <div className="mt-1"><MaturityBadge maturity={ver.verified_maturity} /></div>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900 p-6">
                  <div className="text-xs uppercase tracking-wider text-slate-400">Ajuste</div>
                  <div className={`font-mono text-4xl font-bold mt-2 flex items-center gap-2 ${ver.delta < 0 ? "text-red-400" : ver.delta > 0 ? "text-emerald-400" : "text-slate-300"}`}>
                    {ver.delta > 0 ? <TrendingUp size={26} /> : ver.delta < 0 ? <TrendingDown size={26} /> : null}
                    {ver.delta > 0 ? "+" : ""}{ver.delta?.toFixed(2)}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Confianza: <span className="capitalize text-slate-300">{ver.confidence}</span> · {ver.evidence_count} evidencia(s)</div>
                </div>
              </div>

              <Section title="Veredicto de la verificación" icon={ShieldCheck}>
                <p className="text-sm text-slate-300 leading-relaxed">{ver.summary}</p>
                {ver.orchestration_assessment && (
                  <p className="text-sm text-slate-400 leading-relaxed mt-3"><span className="text-slate-200 font-medium">Orquestación: </span>{ver.orchestration_assessment}</p>
                )}
              </Section>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <Section title="Corroborado" icon={CheckCircle2}><List items={ver.corroborated} icon={CheckCircle2} iconColor="text-emerald-400" /></Section>
                <Section title="Contradicho" icon={XCircle}><List items={ver.contradicted} icon={XCircle} iconColor="text-red-400" /></Section>
                <Section title="Sin evidencia" icon={AlertTriangle}><List items={ver.gaps} icon={AlertTriangle} iconColor="text-amber-400" /></Section>
              </div>

              <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
                <table className="w-full text-sm" data-testid="verification-table">
                  <thead>
                    <tr className="border-b border-slate-800 text-left text-slate-400">
                      <th className="px-5 py-3 font-medium">Dimensión</th>
                      <th className="px-5 py-3 font-medium text-center">Declarado</th>
                      <th className="px-5 py-3 font-medium text-center">Verificado</th>
                      <th className="px-5 py-3 font-medium hidden md:table-cell">Nota</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {ver.dimension_adjustments?.map((d) => (
                      <tr key={d.key} className="hover:bg-slate-800/50 transition-colors duration-200">
                        <td className="px-5 py-3 font-medium text-slate-100">{d.name}</td>
                        <td className="px-5 py-3 text-center font-mono text-slate-500">{d.declared.toFixed(1)}</td>
                        <td className="px-5 py-3 text-center font-mono font-bold" >
                          <span className={d.verified < d.declared ? "text-red-400" : d.verified > d.declared ? "text-emerald-400" : "text-primary"}>{d.verified.toFixed(1)}</span>
                        </td>
                        <td className="px-5 py-3 text-slate-400 hidden md:table-cell">{d.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Section title="Recomendación del Deputy" icon={Sparkles}>
                <p className="text-sm text-slate-300 leading-relaxed">{ver.recommendation}</p>
              </Section>
            </>
          )}
        </TabsContent>

        {/* Practical case */}
        <TabsContent value="practical" className="mt-6 space-y-5">
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-heading text-lg font-medium tracking-tight text-slate-100 flex items-center gap-2">
                <FlaskConical size={18} className="text-primary" strokeWidth={1.5} /> Prueba práctica de orquestación
              </h3>
              <p className="text-sm text-slate-400 mt-1 max-w-xl">Un mini-caso técnico adaptado al puesto para demostrar en vivo la capacidad de diseñar y orquestar agentes.</p>
            </div>
            <Button data-testid="gen-case-btn" disabled={genCaseMut.isPending} onClick={() => genCaseMut.mutate()} variant={pc ? "outline" : "default"}
              className={pc ? "gap-2 border-slate-700 text-slate-200 hover:bg-slate-800" : "gap-2 bg-primary text-slate-950 hover:bg-cyan-400"}>
              {genCaseMut.isPending ? <><Loader2 size={16} className="animate-spin" /> Generando…</> : <><FlaskConical size={16} /> {pc ? "Regenerar caso" : "Generar prueba"}</>}
            </Button>
          </div>

          {!pc ? (
            <div className="rounded-xl border border-dashed border-slate-800 bg-slate-900/40 p-10 text-center text-slate-500">
              Genera una prueba práctica para evaluar la capacidad real de orquestación del candidato.
            </div>
          ) : (
            <>
              <Section title={pc.title} icon={FlaskConical}>
                <p className="text-sm text-slate-300 leading-relaxed">{pc.scenario}</p>
                <p className="text-sm text-slate-200 mt-3"><span className="text-primary">Objetivo: </span>{pc.objective}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-4">
                  <div><div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Tareas</div><List items={pc.tasks} icon={Cpu} /></div>
                  <div><div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Entregables</div><List items={pc.expected_deliverables} icon={FileText} /></div>
                  <div><div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Rúbrica</div><List items={pc.rubric} icon={Target} /></div>
                </div>
              </Section>

              {pc.evaluation ? (
                <Section title="Evaluación de la respuesta" icon={BadgeCheck}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="font-mono text-4xl font-bold text-primary">{pc.evaluation.score?.toFixed(1)}<span className="text-slate-600 text-lg">/5</span></div>
                    <p className="text-sm text-slate-300 flex-1">{pc.evaluation.verdict}</p>
                  </div>
                  {pc.evaluation.orchestration_quality && <p className="text-sm text-slate-400 mb-4"><span className="text-slate-200 font-medium">Orquestación: </span>{pc.evaluation.orchestration_quality}</p>}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <div><div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Fortalezas</div><List items={pc.evaluation.strengths} icon={CheckCircle2} iconColor="text-emerald-400" /></div>
                    <div><div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Carencias</div><List items={pc.evaluation.weaknesses} icon={XCircle} iconColor="text-amber-400" /></div>
                  </div>
                  {pc.submission?.response_text && (
                    <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950 p-4">
                      <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Respuesta del candidato</div>
                      <p className="text-sm text-slate-400 whitespace-pre-wrap">{pc.submission.response_text}</p>
                    </div>
                  )}
                </Section>
              ) : (
                <Section title="Responder a la prueba" icon={Send}>
                  <Textarea data-testid="practical-answer" value={practicalAnswer} onChange={(e) => setPracticalAnswer(e.target.value)} rows={8}
                    className="bg-slate-950 border-slate-800 focus:border-primary" placeholder="Describe tu solución: workflow, agentes, arquitectura, controles, comparación manual vs con agentes…" />
                  <Button data-testid="submit-practical-btn" disabled={submitPracticalMut.isPending || practicalAnswer.trim().length < 20}
                    onClick={() => submitPracticalMut.mutate()} className="mt-3 gap-2 bg-primary text-slate-950 hover:bg-cyan-400">
                    {submitPracticalMut.isPending ? <><Loader2 size={16} className="animate-spin" /> Evaluando…</> : <><Send size={16} /> Enviar respuesta</>}
                  </Button>
                </Section>
              )}
            </>
          )}
        </TabsContent>


        {/* Interview */}
        <TabsContent value="interview" className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Section title="Preguntas de entrevista sugeridas" icon={MessageSquareQuote}>
            <List items={ev.interview_questions} icon={HelpCircle} />
          </Section>
          <Section title="Riesgos a validar" icon={ShieldAlert}>
            <List items={ev.risks} icon={ShieldAlert} iconColor="text-red-400" />
          </Section>
        </TabsContent>

        {/* Consultant report */}
        <TabsContent value="consultant" className="mt-6 space-y-5">
          <Section title="Resumen para el consultor">
            <p className="text-sm text-slate-300 leading-relaxed">{cr.summary}</p>
          </Section>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Section title="Aplicabilidad al puesto" icon={Target}>
              <p className="text-sm text-slate-300 leading-relaxed">{cr.applicability}</p>
            </Section>
            <Section title="Preguntas pendientes" icon={HelpCircle}>
              <List items={cr.pending_questions} icon={HelpCircle} />
            </Section>
          </div>
          <Section title="Recomendación del Deputy" icon={Sparkles}>
            <p className="text-sm text-slate-300 leading-relaxed">{cr.recommendation}</p>
          </Section>

          {isConsultant && (
            <Section title="Conclusiones del consultor (decisión humana)" icon={BadgeCheck}>
              <div className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-400">Notas internas</label>
                  <Textarea data-testid="consultant-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="mt-1.5 bg-slate-950 border-slate-800 focus:border-primary" placeholder="Tus observaciones…" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-400">Recomendación final</label>
                  <Textarea data-testid="consultant-recommendation" value={recommendation} onChange={(e) => setRecommendation(e.target.value)} rows={3} className="mt-1.5 bg-slate-950 border-slate-800 focus:border-primary" placeholder="Tu recomendación de contratación…" />
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button data-testid="save-notes-btn" onClick={() => saveMut.mutate({ notes, final_recommendation: recommendation })} disabled={saveMut.isPending} className="gap-2 bg-primary text-slate-950 hover:bg-cyan-400">
                    <Save size={16} /> Guardar
                  </Button>
                  <Button
                    data-testid="validate-btn"
                    variant="outline"
                    onClick={() => saveMut.mutate({ validated: !a.consultant_notes?.validated })}
                    className="gap-2 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                  >
                    <BadgeCheck size={16} /> {a.consultant_notes?.validated ? "Quitar validación" : "Validar evaluación"}
                  </Button>
                </div>
              </div>
            </Section>
          )}

          {!isConsultant && a.consultant_notes?.final_recommendation && (
            <Section title="Recomendación final del consultor">
              <p className="text-sm text-slate-300 leading-relaxed">{a.consultant_notes.final_recommendation}</p>
            </Section>
          )}
        </TabsContent>

        {/* Client report */}
        <TabsContent value="client" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button data-testid="download-pdf-btn" onClick={downloadPdf} disabled={downloading} className="gap-2 bg-primary text-slate-950 hover:bg-cyan-400">
              <Download size={16} /> {downloading ? "Generando…" : "Descargar informe PDF"}
            </Button>
          </div>
          <div className="rounded-xl border border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950 overflow-hidden">
            <div className="relative h-32 overflow-hidden">
              <img src="https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="w-full h-full object-cover opacity-40" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 to-transparent" />
              <div className="absolute bottom-4 left-6">
                <div className="text-xs uppercase tracking-[0.2em] text-primary">Informe ejecutivo · Talent Hackers</div>
                <div className="font-heading text-2xl font-bold tracking-tight">{a.candidate_name}</div>
              </div>
            </div>
            <div className="p-6 md:p-8 space-y-6">
              <div className="flex flex-wrap items-center gap-6">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400">Agentic Productivity Index</div>
                  <div className="font-mono text-4xl font-bold text-primary">{ev.agentic_index?.toFixed(2)}</div>
                </div>
                <MaturityBadge maturity={ev.maturity} />
                <div className="text-sm text-slate-400">Puesto: <span className="text-slate-200">{a.target_role}</span></div>
              </div>
              <div>
                <h3 className="font-heading text-lg font-medium tracking-tight mb-2">Resumen ejecutivo</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{clr.executive_summary}</p>
              </div>
              <div>
                <h3 className="font-heading text-lg font-medium tracking-tight mb-3">Propuesta de valor</h3>
                <List items={clr.value_proposition} icon={CheckCircle2} iconColor="text-emerald-400" />
              </div>
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                <div className="text-xs uppercase tracking-wider text-primary mb-1">Productividad aumentada</div>
                <p className="text-sm text-slate-300 leading-relaxed">{clr.productivity_note}</p>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
